// ============================================================
// EffectsGlance - the session sheet's Effects section, at a glance (plan
// modes-rework, stage 5): three lines, each bundle with a dot - lit while
// it is up now -
//
//   Always on   his Always on items (a trigger lit while its state holds)
//   Solo modes  his Solo modes (lit while switched on; none in Squad Mode)
//   Squad       his kill-team's Oath, his mode and the abilities he has
//
// and **Edit**, which unfolds the parts to change them in place
// (`EffectParts`). Switching is the actor's strip's; what he carries this
// moment is the Status tab's.
// ============================================================
import { useState, type ReactNode } from 'react';
import { useKillTeamsContext } from '../../contexts/KillTeamsContext';
import { resolveDefenderSheet } from '../../engine/character/combatStatsResolver';
import { upNow } from '../../engine/character/soloModes';
import { describeExposure } from '../../engine/exposures';
import type { Character } from '../../engine/character/types';
import type { CombatState } from '../../engine/roster/types';
import common from '../../styles/common.module.css';
import styles from '../../styles/Stances.module.css';

interface Props {
  character: Character;
  /** His live state: what is up, and his Squad Mode. */
  state: CombatState | null | undefined;
  /** The parts, unfolded by Edit. */
  children: ReactNode;
}

/** His innate exposures, read only (the editor's Exposures, the traits' own): what others always see of him. */
export function InnateLine({ character }: { character: Character }) {
  const innate = resolveDefenderSheet(character).exposures;
  if (!innate.length) return null;
  return (
    <span data-testid="effects-innate">
      {innate.map((code) => describeExposure(code) ?? code).join(' · ')}
      <span className={styles.says}> · innate</span>
    </span>
  );
}

/** "● Asalto · ○ Wrath": each name with a dot, lit while it is up. */
const dotted = (items: readonly { name: string; up: boolean }[]) =>
  items.map((i) => `${i.up ? '●' : '○'} ${i.name}`).join(' · ');

/**
 * A brother's body, as D8 reads it off the sheet: a person (no horde, crowd or vehicle) and no network's subordinate.
 * The roster's full test (`isBrother`) reads the projection; the sheet says the same for these.
 */
const brotherBody = (c: Character) =>
  !c.horde?.isHorde && (!c.bodyType || c.bodyType === 'humanoid') && !(c.control?.tag && !c.control.isController);

/** "Oath of the Astartes · In Squad Mode · ● Bolter Assault", or that he is on no kill-team. */
function squadLine(
  character: Character,
  state: Props['state'],
  oath: { name: string; abilities: { name: string }[] } | null,
) {
  if (!oath || !brotherBody(character)) return 'not on a kill-team';
  const ks = state?.killTeam;
  const has = new Set((ks?.joined ?? []).map((j) => j.name));
  const mode = ks?.on ? 'in Squad Mode' : ks?.pendingEntry ? 'enters at his next turn' : 'in Solo Mode';
  const abilities = dotted(oath.abilities.map((a) => ({ name: a.name, up: has.has(a.name) })));
  return [oath.name, mode, abilities].filter(Boolean).join(' · ');
}

function Line({ label, text, testId }: { label: string; text: string; testId: string }) {
  return (
    <span data-testid={testId}>
      <span className={styles.says}>{label} </span>
      {text}
    </span>
  );
}

export default function EffectsGlance({ character, state, children }: Props) {
  const [open, setOpen] = useState(false);
  const up = new Set(upNow(character, state).map((u) => u.key));
  const k = useKillTeamsContext();
  const tag = character.team?.tag;
  const oath = (tag && k?.teams.killTeams[tag]?.oath) || null;
  const always = (character.alwaysOn ?? []).map((a) => ({ name: a.name, up: up.has(`a:${a.id}`) }));
  const solo = (character.soloModes ?? []).map((m) => ({ name: m.name, up: up.has(`s:${m.id}`) }));
  return (
    <div className={styles.effectSection} data-testid="effects-glance">
      <Line label="Always on" text={dotted(always) || 'none'} testId="glance-always" />
      <Line label="Solo modes" text={dotted(solo) || 'none'} testId="glance-solo" />
      <Line label="Squad" text={squadLine(character, state, oath)} testId="glance-squad" />
      <button type="button" className={common.btnSmall} data-testid="glance-edit" onClick={() => setOpen((o) => !o)}>
        {open ? 'Done' : 'Edit'}
      </button>
      {open && children}
    </div>
  );
}
